import { useCallback } from 'react';
import { CreatePriceRoundingRuleConfigDTO, RoundingRuleRangeDTO } from '@retail/rounding/types';
import {
  useCreatePriceRoundingRuleSet,
  useDeletePriceRoundingRuleSet,
  useSimulatePriceRoundingRuleSet,
  useUpdatePriceRoundingRuleSet,
} from '@retail/rounding/data-access';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@retail/hooks';
import { useRoundingTFunction } from '@retail/rounding/i18n';
import { useDisclosure } from '@shared/hooks';

export const useRoundingRuleActions = () => {
  const { assortmentId } = useSelectedOrgUnit();
  const toast = useToast();
  const t = useRoundingTFunction();
  const queryClient = useQueryClient();
  const conditionInUse = useDisclosure();

  const { mutateAsync: mutateCreateRoundingRuleSet, isPending: isLoadingRoundingRuleSetCreation } =
    useCreatePriceRoundingRuleSet({
      assortmentId,
    });

  const { mutateAsync: mutateUpdateRoundingRuleSet, isPending: isLoadingUpdateRuleSet } =
    useUpdatePriceRoundingRuleSet();

  const { mutateAsync: mutateDeleteRoundingRuleSet, isPending: isLoadingDeleteRuleSet } =
    useDeletePriceRoundingRuleSet();

  const {
    mutateAsync: mutateSimulateRoundingRuleSet,
    data: simulationResult,
    isPending: isLoadingRoundingRuleSetSimulation,
    reset: resetSimulationResult,
  } = useSimulatePriceRoundingRuleSet();

  const createRoundingRuleSet = useCallback(
    async (dto: CreatePriceRoundingRuleConfigDTO) => {
      mutateCreateRoundingRuleSet({ body: dto })
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: ['priceRounding', assortmentId],
          });
          toast.success(t('rounding.feedback.create.success'));
        })
        .catch((err) => {
          console.error(err);
          toast.error(t('rounding.feedback.create.error'));
          throw err;
        });
    },
    [mutateCreateRoundingRuleSet, queryClient, assortmentId, t, toast]
  );

  const updateRoundingRuleSet = useCallback(
    async (dto: CreatePriceRoundingRuleConfigDTO, ruleSetId: number) => {
      mutateUpdateRoundingRuleSet({
        body: dto,
        urlVariables: { ruleSetId },
      })
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: ['priceRounding', assortmentId],
          });
          toast.success(t('rounding.feedback.update.success'));
        })
        .catch((err) => {
          console.error(err);
          toast.error(t('rounding.feedback.update.error'));
          throw err;
        });
    },
    [mutateUpdateRoundingRuleSet, queryClient, assortmentId, t, toast]
  );

  const deleteRoundingRuleSet = useCallback(
    async (ruleSetId: number) => {
      return mutateDeleteRoundingRuleSet({ urlVariables: { ruleSetId } })
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: ['priceRounding', assortmentId],
          });
          toast.success(t('rounding.feedback.delete.success'));
        })
        .catch((err) => {
          if (err.status === 400) {
            conditionInUse.onOpen();
          } else {
            console.error(err);
            toast.error(t('rounding.feedback.delete.error'));
          }

          throw err;
        });
    },
    [mutateDeleteRoundingRuleSet, queryClient, assortmentId, toast, t, conditionInUse]
  );

  const simulatePrices = useCallback(
    async (simulationPrices: number[], ranges: RoundingRuleRangeDTO[]) => {
      mutateSimulateRoundingRuleSet({
        body: { prices: simulationPrices, intervals: ranges },
      }).catch((err) => {
        console.error(err);
        toast.error(t('rounding.simulation.errors.generic'));
        throw err;
      });
    },
    [mutateSimulateRoundingRuleSet, t, toast]
  );

  return {
    createRoundingRuleSet,
    updateRoundingRuleSet,
    deleteRoundingRuleSet,
    simulatePrices,
    simulationResult,
    isLoadingRoundingRuleSetCreation,
    isLoadingUpdateRuleSet,
    isLoadingDeleteRuleSet,
    isLoadingRoundingRuleSetSimulation,
    conditionInUse,
    resetSimulationResult,
  };
};
